/**
 * What a Family Builder failure means on screen — pure, so the mapping is
 * testable without a pane.
 *
 * The agent's errors are `AppError`s: `{ success: false, error: { code, message,
 * details } }`. For anything that touches the family's RFA the code is one of v2's
 * `FAMILY_RFA_*` codes, mapped from the Revit command's `[MARKER]`, and
 * `details.retryAction` says which button can recover it — Retry (open again),
 * Locate (pick the RFA's new place), or none. That is the whole contract: the UI
 * never parses the human sentence to decide what to offer (v2's
 * `parseBindingFailure`, ported).
 *
 * One code is the desktop's own: `INSTANCE_BUSY`. The agent holds the instance
 * lock across a family turn, an activation and an execute (#255 fix 3), so a chat
 * or a skill run on the same Revit refuses with it. It reads as "busy" with Retry,
 * the state v2 already has for a Revit that cannot take a request right now.
 */
import type { FamilyRetryAction, FamilyRfaBindingState } from "./types";

/** A refused `/family-builder` request, with the agent's own code — not normalised. */
export class FamilyBuilderError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;
  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = "FamilyBuilderError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Response status + body → the error, keeping the agent's raw code and details. */
export function readFamilyError(status: number, body: unknown, statusText = ""): FamilyBuilderError {
  const b = isRecord(body) ? body : {};
  const error = isRecord(b["error"]) ? b["error"] : {};
  const details = isRecord(error["details"]) ? error["details"] : isRecord(b["details"]) ? b["details"] : undefined;
  const message =
    typeof error["message"] === "string" && error["message"].trim()
      ? error["message"]
      : typeof b["error"] === "string"
        ? b["error"]
        : typeof b["message"] === "string"
          ? b["message"]
          : statusText || `HTTP ${status}`;
  const code =
    typeof error["code"] === "string" ? error["code"] : typeof b["code"] === "string" ? b["code"] : `HTTP_${status}`;
  return new FamilyBuilderError(status, code, message, details);
}

/** v2's code → binding state table, plus the desktop's instance lock. */
// An active-document mismatch keeps the binding ready so switching back permits a new request.
export const BINDING_STATE_BY_CODE: Readonly<Record<string, FamilyRfaBindingState>> = {
  FAMILY_RFA_BINDING_REQUIRED: "binding_required",
  FAMILY_RFA_NOT_FOUND: "not_found",
  FAMILY_RFA_BINDING_CONFLICT: "conflict",
  FAMILY_RFA_UNSAVED: "unsaved",
  FAMILY_RFA_SAVE_FAILED: "save_failed",
  FAMILY_RFA_OPEN_FAILED: "error",
  FAMILY_RFA_ACTIVATION_FAILED: "error",
  FAMILY_RFA_REOPEN_REQUIRED: "error",
  FAMILY_RFA_BUSY: "busy",
  FAMILY_RFA_OPEN_CANCELLED: "cancelled",
  FAMILY_RFA_REQUEST_EXPIRED: "expired",
  INSTANCE_BUSY: "busy",
};

export interface BindingFailure {
  state: FamilyRfaBindingState;
  /** The agent's sentence, with the Revit-side cause when it adds something. */
  message: string;
  path: string | null;
  /** The error concerns the RFA, so the banner — not only the chat — must say so. */
  isBindingError: boolean;
  retryAction?: FamilyRetryAction;
  code: string | null;
}

export function readRetryAction(value: unknown): FamilyRetryAction | undefined {
  return value === "retry" || value === "locate" || value === "none" ? value : undefined;
}

/** Any thrown value → what the RFA banner shows. v2's `parseBindingFailure`. */
export function bindingFailure(error: unknown): BindingFailure {
  const coded = error as { code?: unknown; details?: unknown } | null;
  const code = typeof coded?.code === "string" ? coded.code : null;
  const details = isRecord(coded?.details) ? coded.details : undefined;
  const message = error instanceof Error ? error.message : String(error);
  const cause = details?.["cause"];
  const additionalCause =
    typeof cause === "string" && cause.trim() && !cause.includes(message) && !message.includes(cause) ? cause : null;
  const state = code ? BINDING_STATE_BY_CODE[code] : undefined;
  const path = details?.["rfaPath"];
  const retryAction = readRetryAction(details?.["retryAction"]);
  return {
    state: state ?? "error",
    message: additionalCause ? `${message}\n${additionalCause}` : message,
    path: typeof path === "string" ? path : null,
    isBindingError: Boolean(state),
    ...(retryAction ? { retryAction } : {}),
    code,
  };
}

/** The banner's sentence for each state. `ready` speaks only about the 3D view focus. */
export function bindingMessageKey(state: FamilyRfaBindingState, focusPending: boolean, code?: string | null): string {
  if (code === "FAMILY_RFA_REOPEN_REQUIRED") return "familyBuilder.reopenRfaRequired";
  if (state === "ready") return focusPending ? "familyBuilder.rfaFocusPending" : "familyBuilder.rfaFocusFailed";
  if (state === "busy" && code === "INSTANCE_BUSY") return "familyBuilder.instanceBusy";
  return (
    {
      activating: "familyBuilder.rfaActivating",
      binding_required: "familyBuilder.rfaBindingRequired",
      not_found: "familyBuilder.rfaNotFound",
      conflict: "familyBuilder.rfaConflict",
      unsaved: "familyBuilder.rfaUnsaved",
      save_failed: "familyBuilder.rfaSaveFailed",
      busy: "familyBuilder.rfaBusy",
      cancelled: "familyBuilder.rfaOpenCancelled",
      expired: "familyBuilder.rfaRequestExpired",
      error: "familyBuilder.rfaActivationFailed",
    } as Record<string, string>
  )[state] ?? "familyBuilder.rfaActivationFailed";
}

/**
 * Locate rather than open: the agent said so, or — from an agent that sends no
 * `retryAction` — the state is one only a new location can fix.
 */
export function shouldLocate(state: FamilyRfaBindingState, retryAction: FamilyRetryAction | undefined): boolean {
  return (
    retryAction === "locate" ||
    (retryAction === undefined && (state === "not_found" || state === "conflict" || state === "binding_required"))
  );
}

/** Whether the banner offers a button at all. v2's `canRetry`. */
export function canRetryBinding(state: FamilyRfaBindingState, retryAction: FamilyRetryAction | undefined): boolean {
  if (state === "ready" || state === "activating") return false;
  if (retryAction === "none") return false;
  if (retryAction === "retry" || retryAction === "locate") return true;
  return ["not_found", "binding_required", "conflict", "busy", "cancelled", "expired", "error"].includes(state);
}

export function familyDocumentErrorKey(message: string, code?: string | null): string | null {
  if (code === "FAMILY_RFA_REOPEN_REQUIRED" || message.includes("[RFA_REOPEN_REQUIRED]"))
    return "familyBuilder.reopenRfaRequired";
  if (code === "FAMILY_RFA_ACTIVE_REQUIRED" || message.includes("[ACTIVE_RFA_REQUIRED]") ||
      message.includes("Open a Revit Family (.rfa) document"))
    return "familyBuilder.activeRfaRequired";
  return null;
}
